import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import { useWishlist } from '../../context/WishlistContext';
import { MrgHeader } from './MrgHeader';
import { MrgFooter } from './MrgFooter';

// Extracted components
import { MrgHero } from './components/MrgHero';
import { MrgCategoryTiles } from './components/MrgCategoryTiles';
import { ProductShowcase, GoogleReviews, BlogSection, RecentlyViewedSection } from './components/HomeSections';
import { BrandsCarousel } from './components/BrandsCarousel';
import { MrgStoreSections } from './components/MrgStoreSections';
import { SoundWaveBg } from './components/SoundWaveBg';
import ShopView from './components/ShopView';
import FloatingCart from './components/FloatingCart';
import SeoHead from '../../components/SeoHead';

// ── Audioprothèse : section "Bientôt disponible" ──
const AudioComingSoon = ({ colors }) => (
  <section id="audio-coming-soon" className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${'#1F2933'}, ${colors.primary})` }} data-testid="audio-coming-soon">
    <SoundWaveBg primary="#ffffff" accent={colors.accent} className="opacity-[0.12]" />
    <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center">
      <div>
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] mb-5"
          style={{ backgroundColor: colors.accent + '26', color: '#fff', border: `1px solid ${colors.accent}66` }}>
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: colors.accent }} />
          Bientôt disponible
        </span>
        <h2 className="text-white font-semibold leading-[1.05]" style={{ fontFamily: "'Poppins', sans-serif", fontSize: 'clamp(30px, 4.5vw, 50px)' }}>
          L&apos;Audioprothèse arrive chez MRG
        </h2>
        <p className="text-white/75 mt-4 text-[15px] md:text-[16px] max-w-lg leading-relaxed">
          Bilans auditifs, appareils auditifs des plus grandes marques et suivi personnalisé.
          Notre pôle audition ouvre prochainement à Istres. Laissez-nous vos coordonnées pour être prévenu en avant-première.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <a href="mailto:contact@mrg-istres.fr?subject=Audioprothèse%20-%20Être%20informé"
            className="px-7 py-3.5 rounded-full text-[14px] font-bold transition-transform hover:scale-105"
            style={{ backgroundColor: colors.accent, color: '#ffffff' }} data-testid="audio-notify-btn">
            Être informé du lancement
          </a>
          <span className="text-white/55 text-[13px]">Bilan auditif gratuit à l&apos;ouverture</span>
        </div>
      </div>
      <div className="relative">
        <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 aspect-[4/3]">
          <img src="https://images.unsplash.com/photo-1623376550324-d406ae677c04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
            alt="Audioprothèse MRG - bientôt disponible" className="w-full h-full object-cover" loading="lazy" />
        </div>
      </div>
    </div>
  </section>
);

const MagereStorefront = ({
  store, template, products, categories, cmsPages,
  search, setSearch, selectedCategory, setSelectedCategory,
  sort, setSort, view, setView, addItem, itemCount, setIsOpen, storeSlug,
  cartItems, removeItem, updateQuantity, cartTotal, standalone = false,
  userType = 'particulier'
}) => {
  const id = useMemo(() => store?.identity || {}, [store]);
  const colors = useMemo(() => ({
    primary: id.primary_color || '#c2185b',
    dark: '#2b2b2b',
    darkFooter: '#1a1a1a',
    text: '#ffffff',
    accent: id.accent_color || '#ff6900',
  }), [id]);

  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;
  const isPro = userType === 'pro';

  const logoUrl = id.logo_light || '';
  const goCategory = (cid) => { setSelectedCategory(cid); setView('shop'); };
  const [featured, setFeatured] = useState({ featured: [], seasonal: [], promos: [] });
  const [expandedCats, setExpandedCats] = useState({});
  const didInitExpand = useRef(false);
  const [displayLimit, setDisplayLimit] = useState(24);
  const { recentlyViewed } = useRecentlyViewed(storeSlug);
  const { isInWishlist, toggleWishlist, fetchWishlistBySlug } = useWishlist();

  const parentCats = useMemo(() => categories.filter(c => !c.parent_id && c.slug !== 'promotion'), [categories]);
  const API = process.env.REACT_APP_BACKEND_URL;

  /* Local search state */
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [floatingCartOpen, setFloatingCartOpen] = useState(false);
  const [productView, setProductView] = useState('grid');
  const [priceRange, setPriceRange] = useState([0, 9999]);
  const [homeTab, setHomeTab] = useState('bestsellers');

  useEffect(() => {
    if (search) { setSearchInput(search); setDebouncedSearch(search); }
  }, [search]);

  /* Debounce search input */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const location = useLocation();

  /* Deep links: read ?cat= / ?q= from URL (reacts to header & footer navigation) */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('cat');
    const q = params.get('q');
    if (cat) { setSelectedCategory(cat); setView('shop'); }
    if (q) { setSearchInput(q); setDebouncedSearch(q); setView('shop'); }
  }, [location.search]);

  /* Filtres : déplier "Lunettes de soleil" + "Lunettes de vue" par défaut */
  useEffect(() => {
    if (didInitExpand.current || !categories.length) return;
    didInitExpand.current = true;
    const exp = {};
    categories.forEach(c => {
      if (['lunettes-de-soleil', 'lunettes-de-vue'].includes(c.slug)) { exp[c.slug] = true; exp[c.id] = true; }
    });
    setExpandedCats(exp);
  }, [categories]);

  /* Set max price from products */
  useEffect(() => {
    if (products.length > 0) {
      const max = Math.ceil(Math.max(...products.map(p => parseFloat(p.price) || 0)));
      setPriceRange(prev => [prev[0], prev[1] === 9999 ? max : prev[1]]);
    }
  }, [products]);

  /* Fetch CMS pages for footer */
  const [pages, setPages] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/pages`)
      .then(r => r.json())
      .then(d => setPages(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [storeSlug, API]);

  /* Fetch delivery info */
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/delivery-info`)
      .then(r => r.json())
      .then(d => setDeliveryInfo(d))
      .catch(() => {});
  }, [storeSlug, API]);

  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/featured`).then(r => r.json()).then(setFeatured).catch(() => {});
  }, [storeSlug, API]);

  /* SEO: Dynamic document title + meta */
  useEffect(() => {
    if (!store) return;
    const name = store?.identity?.company_name || store?.name || 'MRG Optique & Audition';
    const baseline = store?.identity?.hero_subtitle || 'Opticien & Audioprothésiste à Istres';
    document.title = `${name} — ${baseline}`;
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', `${name} — ${baseline}. Lunettes de soleil et de vue, grandes marques, lentilles. Solutions auditives bientôt disponibles. Prise de rendez-vous en ligne.`);
    setMeta('property', 'og:title', `${name} — ${baseline}`);
    setMeta('property', 'og:description', `Opticien à Istres : lunettes de soleil et de vue des plus grandes marques (Ray-Ban, Gucci, Maui Jim…), lentilles. Audioprothèse bientôt disponible.`);
    setMeta('name', 'keywords', 'opticien Istres, opticien 13800, lunettes de soleil Istres, lunettes de vue Istres, lentilles de contact Istres, Ray-Ban Istres, Gucci, Maui Jim, audioprothésiste Istres, tiers payant mutuelle, MRG Optique Audition');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', name);
    if (store?.identity?.logo_url) setMeta('property', 'og:image', store.identity.logo_url);
  }, [store]);

  /* Reset display limit when changing filters */
  useEffect(() => { setDisplayLimit(24); }, [selectedCategory, debouncedSearch, sort, priceRange]);

  /* Fetch reviews */
  const [reviews, setReviews] = useState([]);
  const [reviewMeta, setReviewMeta] = useState({ rating: 4.9, total: 0, url: '' });
  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/google-reviews`)
      .then(r => r.json())
      .then(d => { setReviews(d.reviews || []); setReviewMeta({ rating: d.rating, total: d.total, url: d.url }); })
      .catch(() => {});
  }, [storeSlug, API]);

  /* Fetch blog posts */
  const [blogPosts, setBlogPosts] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}/blog?featured=true&limit=6`)
      .then(r => r.json())
      .then(d => {
        const posts = d.posts || [];
        if (posts.length === 0) {
          fetch(`${API}/api/s/${storeSlug}/blog?limit=3`)
            .then(r2 => r2.json())
            .then(d2 => setBlogPosts(d2.posts || []))
            .catch(() => {});
        } else {
          setBlogPosts(posts);
        }
      })
      .catch(() => {});
  }, [storeSlug, API]);

  /* Shared props for ProductCard components */
  const cardProps = {
    colors, categories, getLink, isInWishlist, toggleWishlist, store, addItem, storeSlug, isPro
  };

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden" data-testid="magere-storefront">
      <SeoHead
        title={`${store?.name || 'MRG Optique & Audition'} — Opticien & Audioprothésiste à Istres · Lunettes, lentilles, audition`}
        description={`${store?.name || 'MRG Optique & Audition'} à Istres (13800) — Lunettes de soleil et de vue, lentilles, solutions auditives. Tiers payant mutuelles. Prise de rendez-vous en ligne.`}
        image={store?.identity?.logo_url || store?.identity?.hero_image_url}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": ["LocalBusiness", "Optician", "HealthAndBeautyBusiness"],
            "@id": typeof window !== 'undefined' ? window.location.origin : undefined,
            "name": store?.name || "MRG Optique & Audition",
            "image": store?.identity?.logo_url,
            "description": "Opticien & Audioprothésiste à Istres — Lunettes de soleil, lunettes de vue, lentilles et solutions auditives. Tiers payant mutuelles.",
            "url": typeof window !== 'undefined' ? window.location.origin : undefined,
            "telephone": store?.identity?.contact_phone || store?.identity?.phone || "+33442489861",
            "priceRange": "€€",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": store?.identity?.address || "Centre Médical, 44 Chemin du Bord de Crau",
              "addressLocality": store?.identity?.city || "Istres",
              "postalCode": store?.identity?.postal_code || "13800",
              "addressCountry": "FR",
            },
            "openingHoursSpecification": [
              { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "09:00", "closes": "13:00" },
              { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "14:00", "closes": "19:00" },
            ],
            "sameAs": [
              store?.identity?.social_facebook || store?.identity?.facebook_url,
              store?.identity?.social_instagram || store?.identity?.instagram_url,
            ].filter(Boolean),
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": typeof window !== 'undefined' ? window.location.origin : undefined,
            "name": store?.name || "MRG Optique & Audition",
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${typeof window !== 'undefined' ? window.location.origin : ''}/?search={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Où se trouve MRG Optique & Audition à Istres ?",
                "acceptedAnswer": { "@type": "Answer", "text": "Notre boutique se situe Centre Médical, 44 Chemin du Bord de Crau, 13800 Istres. Parking gratuit à proximité immédiate." },
              },
              {
                "@type": "Question",
                "name": "Acceptez-vous le tiers payant mutuelles ?",
                "acceptedAnswer": { "@type": "Answer", "text": "Oui, nous appliquons le tiers payant immédiat avec la quasi-totalité des mutuelles françaises : Almerys, Carte Blanche, Itelis, Kalivia, Santéclair, Viamédis, et bien d'autres. Présentez simplement votre carte de mutuelle en magasin." },
              },
              {
                "@type": "Question",
                "name": "Puis-je prendre rendez-vous en ligne ?",
                "acceptedAnswer": { "@type": "Answer", "text": "Oui, vous pouvez réserver votre rendez-vous directement depuis notre site, 7j/7 et 24h/24, pour un examen de vue, un essai de lentilles ou un bilan auditif." },
              },
              {
                "@type": "Question",
                "name": "Quelles marques de lunettes vendez-vous ?",
                "acceptedAnswer": { "@type": "Answer", "text": "Nous proposons plus de 30 grandes marques : Ray-Ban, Persol, Tom Ford, Oakley, Maui Jim, Vuarnet, Police, Carrera, Bollé, Lacoste, Guess, et bien d'autres." },
              },
              {
                "@type": "Question",
                "name": "Le 100% Santé est-il appliqué chez MRG Optique ?",
                "acceptedAnswer": { "@type": "Answer", "text": "Oui, nous proposons le panier 100% Santé (classe A) qui permet de s'équiper d'une paire complète de lunettes ou d'un appareil auditif sans aucun reste à charge, en combinant la prise en charge Sécurité Sociale et mutuelle responsable." },
              },
            ],
          },
        ]}
      />
      <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Jost:wght@300;400;500;600;700;900&family=EB+Garamond:ital,wght@0,400;1,400&display=swap" rel="stylesheet" />
      <MrgHeader
        store={store}
        categories={parentCats}
        onBoutiqueClick={() => setView('shop')}
        onCartClick={() => setFloatingCartOpen(prev => !prev)}
        onHomeClick={() => setView('home')}
        onCategorySelect={(cid) => goCategory(cid)}
        standalone={standalone}
      />
      <main className="flex-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
        {view === 'home' ? (
          <>
            <MrgHero id={id} colors={colors} setView={setView} />
            <MrgCategoryTiles parentCats={parentCats} goCategory={goCategory} colors={colors} />
            <BrandsCarousel colors={colors} />
            <ProductShowcase featured={featured} homeTab={homeTab} setHomeTab={setHomeTab} colors={colors} setView={setView} cardProps={cardProps} />
            <MrgStoreSections store={store} colors={colors} />
            <GoogleReviews reviews={reviews} meta={reviewMeta} colors={colors} id={id} />
            <BlogSection blogPosts={blogPosts} colors={colors} getLink={getLink} />
            <RecentlyViewedSection recentlyViewed={recentlyViewed} colors={colors} getLink={getLink} />
          </>
        ) : (
          <>
            <ShopView
              products={products} categories={categories} parentCats={parentCats} colors={colors}
              sort={sort} setSort={setSort} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
              searchInput={searchInput} setSearchInput={setSearchInput} debouncedSearch={debouncedSearch}
              displayLimit={displayLimit} setDisplayLimit={setDisplayLimit} featured={featured}
              id={id} goCategory={goCategory} getLink={getLink} addItem={addItem} storeSlug={storeSlug}
              isPro={isPro} isInWishlist={isInWishlist} toggleWishlist={toggleWishlist} store={store}
              priceRange={priceRange} setPriceRange={setPriceRange} productView={productView} setProductView={setProductView}
              expandedCats={expandedCats} setExpandedCats={setExpandedCats}
            />
            <RecentlyViewedSection recentlyViewed={recentlyViewed} colors={colors} getLink={getLink} />
          </>
        )}
      </main>
      <MrgFooter store={store} pages={pages} categories={categories} standalone={standalone} />

      <FloatingCart
        cartItems={cartItems} removeItem={removeItem} updateQuantity={updateQuantity} cartTotal={cartTotal} addItem={addItem}
        colors={colors} getLink={getLink} storeSlug={storeSlug} deliveryInfo={deliveryInfo} products={products}
        floatingCartOpen={floatingCartOpen} setFloatingCartOpen={setFloatingCartOpen}
      />

      <style>{`
        @keyframes waveSlow { 0%,100%{d:path("M0,40 C180,80 360,0 540,40 C720,80 900,0 1080,40 C1260,80 1440,0 1440,40 L1440,80 L0,80 Z")} 50%{d:path("M0,50 C180,10 360,70 540,30 C720,60 900,20 1080,50 C1260,10 1440,70 1440,50 L1440,80 L0,80 Z")} }
        @keyframes waveSlower { 0%,100%{d:path("M0,50 C200,20 400,70 600,40 C800,10 1000,60 1200,35 C1350,15 1440,50 1440,50 L1440,80 L0,80 Z")} 50%{d:path("M0,30 C200,60 400,10 600,50 C800,70 1000,20 1200,55 C1350,75 1440,30 1440,30 L1440,80 L0,80 Z")} }
        .animate-wave-slow { animation: waveSlow 8s ease-in-out infinite; }
        .animate-wave-slower { animation: waveSlower 12s ease-in-out infinite; }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        .animate-fadeInUp { animation: fadeInUp 0.8s ease-out; }
        @keyframes drawUnderline { from { width:0; opacity:0; } to { width:100%; opacity:1; } }
        .animate-drawUnderline { animation: drawUnderline 0.8s ease-out 1.4s forwards; }
        @keyframes scrollPromos { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-scrollPromos { animation: scrollPromos 25s linear infinite; display:flex; gap:16px; }
        .animate-scrollPromos:hover { animation-play-state: paused; }
        @keyframes marqueeScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-33.33%); } }
        .animate-marquee { animation: marqueeScroll 30s linear infinite; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default MagereStorefront;
